// Shared helpers for building/parsing the IELTS model-test blueprint stored on Quiz.metadata.
// Mirrors toeflBlueprint.js: the taker (IeltsModelTest) renders section → module → task from this;
// the admin Add/Edit forms produce it. Keep the shape identical to seed_ielts_mt1.py's _blueprint().

// Section config — Listening is forward-only with one audio per part (up to 4 modules), Reading
// allows going back within a passage (up to 3 modules), Writing/Speaking are single unscored
// modules. Listening/Reading are band-scored (0–9) via named raw→band tables on the backend.
export const IELTS_SECTIONS = [
    { key: 'listening', name: 'Listening', maxModules: 4, navigation: 'forward_only_per_question', scoring: { band_table: 'ielts_listening' }, defaultMinutes: 10 },
    { key: 'reading', name: 'Reading', maxModules: 3, navigation: 'back_within_module', scoring: { band_table: 'ielts_reading' }, defaultMinutes: 20 },
    { key: 'writing', name: 'Writing', maxModules: 1, scoring: { unscored: true }, defaultMinutes: 60 },
    { key: 'speaking', name: 'Speaking', maxModules: 1, scoring: { unscored: true }, defaultMinutes: 14 },
];

export const sectionByName = (name) => IELTS_SECTIONS.find(s => s.name === name);

// A bucket is the assignable target used by the quiz forms. Keys are STABLE regardless of the
// current module count so switching module counts never orphans assigned questions: multi-module
// sections (Listening/Reading) use "Listening — Module 1"; single-module sections use the name.
export function bucketKey(sectionName, moduleIdx) {
    const sec = sectionByName(sectionName);
    const multi = sec && sec.maxModules > 1;
    return multi ? `${sectionName} — Module ${moduleIdx + 1}` : sectionName;
}
export function parseBucketKey(bucket) {
    const m = /^(.*) — Module (\d+)$/.exec(bucket);
    if (m) return { sectionName: m[1], moduleIdx: Number(m[2]) - 1 };
    return { sectionName: bucket, moduleIdx: 0 };
}

// The ordered list of buckets for a given module-count config.
export function bucketsFor(moduleCount) {
    const out = [];
    IELTS_SECTIONS.forEach(sec => {
        const count = sec.maxModules === 1 ? 1 : (moduleCount[sec.key] || 1);
        for (let i = 0; i < count; i += 1) out.push({ sectionKey: sec.key, sectionName: sec.name, moduleIdx: i, count, key: bucketKey(sec.name, i, count) });
    });
    return out;
}

// Derive a taker task type + display title from a question. Prefers the canonical IELTS task
// tag (metadata.ielts.task: note_completion, table_completion, tfng, matching, …) so a rebuilt
// blueprint keeps the same task types jobprep-question generated, not collapsed generic ones.
export function taskInfo(q) {
    const t = q.metadata && q.metadata.ielts && q.metadata.ielts.task;
    if (t) return { task: t, title: q.topic || t };
    if (q.question_type === 'MATCHING') return { task: 'matching', title: q.topic || 'Matching' };
    if (q.question_type === 'TEXT' && q.subject === 'Speaking') return { task: 'speaking', title: q.topic || 'Speaking' };
    if (q.question_type === 'TEXT' && q.subject === 'Writing') return { task: 'writing', title: q.topic || 'Writing' };
    if (q.question_type === 'TEXT') return { task: 'completion', title: q.topic || 'Complete the answer' };
    if (q.question_type === 'MCQ') return { task: 'mcq', title: q.topic || 'Multiple choice' };
    return { task: 'mcq', title: q.topic || 'Question' };
}

// Index an existing blueprint's tasks by each question_id they contain, capturing the fields
// that are NOT reconstructable from the questions alone (layout/template/table for completion,
// group_id, and the exact task type/title). Used on edit to carry these through unchanged.
export function origTaskByQid(metadata) {
    const map = {};
    (metadata?.sections || []).forEach((sec) => {
        (sec.modules || []).forEach((mod) => {
            (mod.tasks || []).forEach((t, i) => {
                const id = `${sec.key || sec.name}:${mod.key || mod.name}:${i}`;
                (t.question_ids || []).forEach((qid) => {
                    map[qid] = {
                        _id: id, task: t.task, title: t.title,
                        layout: t.layout, template: t.template, table: t.table, group_id: t.group_id,
                    };
                });
            });
        });
    });
    return map;
}

// Completion task types whose blanks render as an inline-blank notes/table block. Mirrors
// jobprep-question's _IELTS_COMPLETION_TASKS.
const COMPLETION_TASKS = new Set([
    'note_completion', 'summary_completion', 'sentence_completion', 'form_completion',
    'flowchart_completion', 'flow_chart_completion', 'completion',
]);

// Synthesize a one-line-per-blank notes template from a completion task's question objects when
// it has none — mirrors jobprep-question's _ielts_task_block so manually-authored (Add) or newly
// added completion questions still render as inline blanks rather than plain "Qn." cards.
function synthesizeTemplate(qObjs) {
    const lines = [];
    (qObjs || []).forEach((q) => {
        const no = ((q.metadata && q.metadata.ielts && q.metadata.ielts.no) || '').toString().trim();
        if (!no) return;
        const label = (q.question_text || '').trim();
        lines.push(label ? `${label} (${no})……` : `(${no})……`);
    });
    return lines.join('\n');
}

// Cluster an ordered list of question objects into tasks. When `origByQid` is supplied (edit),
// every question that was already in the quiz keeps its ORIGINAL task, and that task's
// task / title / layout / template / table / group_id are carried through VERBATIM (never
// re-derived or synthesized) — so clicking "Save Changes" cannot alter an existing task's
// wording or completion block. Questions are grouped by original task identity globally (not
// just contiguously), so reordering can't fragment or duplicate a task. Newly added questions
// (no original task) cluster contiguously by (task type + topic), and completion ones get a
// synthesized notes template so they still render as inline blanks.
export function buildTasks(questionObjs, origByQid) {
    const byId = {};
    (questionObjs || []).forEach(q => { byId[q.question_id] = q; });
    const tasks = [];
    const origTaskByKey = {};
    (questionObjs || []).forEach((q) => {
        const orig = origByQid && origByQid[q.question_id];
        if (orig) {
            const key = `orig:${orig._id}`;
            let task = origTaskByKey[key];
            if (!task) {
                // Carry the original task through verbatim — do NOT re-derive any field.
                task = { _key: key, _orig: true, task: orig.task, title: orig.title, question_ids: [] };
                if (orig.layout !== undefined) task.layout = orig.layout;
                if (orig.template !== undefined) task.template = orig.template;
                if (orig.table !== undefined) task.table = orig.table;
                if (orig.group_id !== undefined) task.group_id = orig.group_id;
                origTaskByKey[key] = task;
                tasks.push(task);
            }
            task.question_ids.push(q.question_id);
            return;
        }
        // New question: contiguous grouping by (task type + topic); never merge into an orig task.
        const info = taskInfo(q);
        const groupKey = `new:t${info.task}:${q.topic || ''}`;
        const last = tasks[tasks.length - 1];
        if (last && last._key === groupKey) {
            last.question_ids.push(q.question_id);
        } else {
            const task = { _key: groupKey, task: info.task, title: info.title, question_ids: [q.question_id] };
            if (q.group_id) task.group_id = q.group_id;
            tasks.push(task);
        }
    });
    // Synthesize templates only for NEW completion tasks — original tasks are left untouched.
    tasks.forEach((t) => {
        if (t._orig || t.template || t.table) return;
        if (!COMPLETION_TASKS.has(t.task)) return;
        const tmpl = synthesizeTemplate(t.question_ids.map(id => byId[id]).filter(Boolean));
        if (tmpl) { t.layout = 'notes'; t.template = tmpl; }
    });
    return tasks.map(({ _key, _orig, ...t }) => t);
}

const moduleName = (sec, count, idx) => {
    if (sec.key === 'listening') return `Part ${idx + 1}`;
    if (sec.key === 'reading') return `Passage ${idx + 1}`;
    if (sec.key === 'writing') return 'Writing Tasks';
    if (sec.key === 'speaking') return 'Speaking';
    return `Module ${idx + 1}`;
};

// Build the full blueprint + the per-section question_ids (for QuizQuestionSet) + total minutes.
// questionsByBucket: { "Listening — Module 1": [question objects], "Writing": [...], ... }
// existingMetadata (optional, EDIT): the quiz's current blueprint — its tasks' layout/template/
// table/group_id/task/title are carried through unchanged for questions that were already in it,
// so editing never rewrites the completion blocks jobprep-question generated.
export function buildBlueprint({ moduleCount, durations, questionsByBucket, existingMetadata }) {
    const origByQid = existingMetadata ? origTaskByQid(existingMetadata) : null;
    const questionIdsBySection = {};
    let totalMinutes = 0;
    const sections = IELTS_SECTIONS.map((sec) => {
        const count = sec.maxModules === 1 ? 1 : (moduleCount[sec.key] || 1);
        const modules = [];
        const secIds = [];
        for (let i = 0; i < count; i += 1) {
            const bucket = bucketKey(sec.name, i, count);
            const qObjs = questionsByBucket[bucket] || [];
            if (qObjs.length === 0) continue;
            secIds.push(...qObjs.map(q => q.question_id));
            const mins = Number(durations[bucket]) || sec.defaultMinutes;
            totalMinutes += mins;
            modules.push({ key: `m${i + 1}`, name: moduleName(sec, count, i), duration_minutes: mins, tasks: buildTasks(qObjs, origByQid) });
        }
        if (secIds.length) questionIdsBySection[sec.name] = secIds;
        const out = { key: sec.key, name: sec.name, modules };
        if (sec.navigation) out.navigation = sec.navigation;
        if (sec.scoring) out.scoring = sec.scoring;
        return out;
    }).filter(s => s.modules.length > 0);

    return { blueprint: { test_kind: 'ielts_model_test', sections }, questionIdsBySection, totalMinutes };
}

// Reverse of buildBlueprint for the edit form: reconstruct buckets/moduleCount/durations from an
// existing blueprint + the quiz's questions ({section: [question objects]}).
export function bucketsFromBlueprint(metadata, questionsBySection) {
    const allById = {};
    Object.values(questionsBySection || {}).forEach(arr => (arr || []).forEach(q => { allById[q.question_id] = q; }));
    const questionsByBucket = {};
    const moduleCount = {};
    const durations = {};
    (metadata?.sections || []).forEach((sec) => {
        const count = (sec.modules || []).length || 1;
        moduleCount[sec.key] = count;
        (sec.modules || []).forEach((mod, idx) => {
            const bucket = bucketKey(sec.name, idx, count);
            const qids = (mod.tasks || []).flatMap(t => t.question_ids || []);
            questionsByBucket[bucket] = qids.map(id => allById[id]).filter(Boolean);
            durations[bucket] = mod.duration_minutes;
        });
    });
    return { questionsByBucket, moduleCount, durations };
}
